import type React from 'react'
import { useState, useMemo, useEffect, useRef } from 'react'
import {
  Eye, X, Maximize2, Minimize2, AlertTriangle,
  Code2, Monitor, Palette, Box,
} from 'lucide-react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'

export interface ArtifactContent {
  type: 'html' | 'css' | 'svg' | 'react'
  content: string
  title?: string
  messageId: string
}

// ── Utilities ─────────────────────────────────────────────────────────────────

function hasExternalUrls(html: string): boolean {
  return /(?:src|href)\s*=\s*["']https?:\/\//i.test(html)
}

function wrapHtmlForPreview(html: string, allowExternal = false): string {
  const csp = allowExternal
    ? `default-src * data: blob:; style-src * 'unsafe-inline'; script-src * 'unsafe-inline' 'unsafe-eval'; img-src * data: blob:`
    : `default-src 'none'; style-src 'unsafe-inline'; script-src 'unsafe-inline'; img-src data: blob:`
  if (html.includes('<!DOCTYPE') || html.includes('<html')) {
    return html.replace(/<head([^>]*)>/i, `<head$1><meta http-equiv="Content-Security-Policy" content="${csp}">`)
  }
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><style>*{box-sizing:border-box}body{margin:0;padding:20px;font-family:system-ui,sans-serif;background:#fff;color:#111}</style></head><body>${html}</body></html>`
}

function wrapCssInScaffold(css: string): string {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'"><style>*{box-sizing:border-box}body{margin:0;padding:24px;font-family:system-ui,sans-serif;background:#fff;color:#111}${css}</style></head><body><h1 class="preview-h1">Heading One</h1><h2 class="preview-h2">Heading Two</h2><p class="preview-p">Paragraph text for preview. <a href="#">A link</a>.</p><button class="preview-btn">Button</button><div class="preview-card"><h3>Card Title</h3><p>Card content here.</p></div></body></html>`
}

// React preview requires loading React + Babel from CDN — only called when user explicitly opts in.
// Using DOM methods (never innerHTML) to prevent XSS in error handling.
// CDN scripts are loaded with crossorigin="anonymous"; SRI is omitted because unpkg does not
// publish stable SRI hashes. The iframe runs with sandbox="allow-scripts" only (no allow-same-origin),
// giving it an opaque origin that cannot access the parent frame's data even if CDN were compromised.
function wrapReactForPreview(jsx: string): string {
  const cleaned = jsx
    .replace(/^import\s.*?from\s+['"][^'"]+['"]\s*;?\s*$/gm, '')
    .replace(/^export\s+default\s+/m, 'const __App = ')
    .replace(/^export\s+(const|function|class)\s+(\w+)/m, '$1 __App')
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<script src="https://unpkg.com/react@18.3.1/umd/react.production.min.js" crossorigin="anonymous"></script>
<script src="https://unpkg.com/react-dom@18.3.1/umd/react-dom.production.min.js" crossorigin="anonymous"></script>
<script src="https://unpkg.com/@babel/standalone@7.23.5/babel.min.js" crossorigin="anonymous"></script>
<style>*{box-sizing:border-box}body{margin:0;padding:20px;font-family:system-ui,sans-serif;background:#fff;color:#111}</style>
</head>
<body><div id="root"></div>
<script type="text/babel">
${cleaned}
const rootEl = document.getElementById('root');
const root = ReactDOM.createRoot ? ReactDOM.createRoot(rootEl) : { render: (el) => ReactDOM.render(el, rootEl) };
try {
  root.render(React.createElement(typeof __App !== 'undefined' ? __App : function() { return React.createElement('div', null, 'No default export found'); }));
} catch(e) {
  const pre = document.createElement('pre');
  pre.style.cssText = 'color:red;padding:12px;white-space:pre-wrap;word-break:break-word';
  pre.textContent = e && e.message ? e.message : String(e);
  rootEl.appendChild(pre);
}
</script>
</body></html>`
}

function typeIcon(type: ArtifactContent['type']) {
  switch (type) {
    case 'html': return <Monitor className="h-3.5 w-3.5" />
    case 'css': return <Palette className="h-3.5 w-3.5" />
    case 'svg': return <Box className="h-3.5 w-3.5" />
    case 'react': return <Code2 className="h-3.5 w-3.5" />
  }
}

function typeLabel(type: ArtifactContent['type']): string {
  switch (type) {
    case 'html': return 'HTML'
    case 'css': return 'CSS'
    case 'svg': return 'SVG'
    case 'react': return 'React'
  }
}

function highlightLang(type: ArtifactContent['type']): string {
  switch (type) {
    case 'svg': return 'xml'
    case 'react': return 'tsx'
    default: return type
  }
}

// ── ArtifactCanvas ─────────────────────────────────────────────────────────────

interface ArtifactCanvasProps {
  artifact: ArtifactContent
  onClose: () => void
}

export function ArtifactCanvas({ artifact, onClose }: ArtifactCanvasProps) {
  const isMobile = typeof window !== 'undefined' && (
    window.matchMedia('(max-width: 767px)').matches ||
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches
  )

  const [tab, setTab] = useState<'preview' | 'code'>('preview')
  const [fullscreen, setFullscreen] = useState(false)
  const [allowExternal, setAllowExternal] = useState(false)
  const [externalWarning, setExternalWarning] = useState(false)
  const [allowReact, setAllowReact] = useState(false)
  const [visible, setVisible] = useState(false)
  const dragRef = useRef<{ startY: number; startH: number } | null>(null)
  const [sheetHeight, setSheetHeight] = useState(70) // vh

  // Animate in on mount
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(id)
  }, [])

  const handleClose = () => {
    setVisible(false)
    setTimeout(onClose, 300)
  }

  const srcdoc = useMemo(() => {
    if (artifact.type === 'css') return wrapCssInScaffold(artifact.content)
    if (artifact.type === 'react') {
      if (!allowReact) return ''
      return wrapReactForPreview(artifact.content)
    }
    if (artifact.type === 'html') {
      if (hasExternalUrls(artifact.content) && !allowExternal) {
        setExternalWarning(true)
        return wrapHtmlForPreview(artifact.content.replace(/(?:src|href)\s*=\s*["']https?:\/\/[^"']*/gi, ''))
      }
      return wrapHtmlForPreview(artifact.content, allowExternal)
    }
    return ''
  }, [artifact, allowExternal, allowReact])

  const svgDataUri = useMemo(() => {
    if (artifact.type === 'svg') return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(artifact.content)}`
    return ''
  }, [artifact])

  // Mobile drag handle
  const handleDragStart = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    dragRef.current = { startY: touch.clientY, startH: sheetHeight }
  }
  const handleDragMove = (e: React.TouchEvent) => {
    if (!dragRef.current) return
    const dy = dragRef.current.startY - e.touches[0].clientY
    const newH = Math.max(30, Math.min(95, dragRef.current.startH + (dy / window.innerHeight) * 100))
    setSheetHeight(newH)
  }
  const handleDragEnd = () => {
    if (sheetHeight < 20) handleClose()
    dragRef.current = null
  }

  const title = artifact.title || `${typeLabel(artifact.type)} Artifact`
  // Always true now that 'image' was removed from the type union -- tabs
  // only ever hid for the image case, which never actually fired.
  const showTabs = true

  // ── Mobile bottom sheet ────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div
          className={cn(
            'fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300',
            visible ? 'opacity-100' : 'opacity-0'
          )}
          onClick={handleClose}
        />

        {/* Sheet */}
        <div
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 flex flex-col bg-surface border-t border-border rounded-t-2xl overflow-hidden',
            'transition-transform duration-300 ease-out',
            visible ? 'translate-y-0' : 'translate-y-full'
          )}
          style={{ height: `${sheetHeight}vh` }}
        >
          {/* Drag handle */}
          <div
            className="flex flex-col items-center pt-2 pb-1 shrink-0 cursor-grab touch-none select-none"
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            <div className="w-10 h-1 rounded-full bg-border" />
          </div>

          {/* Header */}
          <CanvasHeader
            title={title}
            type={artifact.type}
            fullscreen={false}
            isMobile={true}
            onClose={handleClose}
            onFullscreen={() => {}}
          />

          {/* Tabs */}
          {showTabs && <CanvasTabs tab={tab} setTab={setTab} />}

          {/* Warnings */}
          {externalWarning && !allowExternal && (
            <ExternalWarning onAllow={() => { setAllowExternal(true); setExternalWarning(false) }} />
          )}
          {artifact.type === 'react' && !allowReact && tab === 'preview' && (
            <ReactCdnWarning onAllow={() => setAllowReact(true)} />
          )}

          {/* Content */}
          <CanvasContent
            artifact={artifact}
            tab={tab}
            srcdoc={srcdoc}
            svgDataUri={svgDataUri}
            allowReact={allowReact}
          />
        </div>
      </>
    )
  }

  // ── Desktop right panel ────────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop (subtle) */}
      <div
        className={cn(
          'fixed inset-0 z-40 transition-opacity duration-300',
          visible ? 'opacity-100' : 'opacity-0'
        )}
        onClick={handleClose}
        style={{ background: 'transparent', pointerEvents: 'none' }}
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed z-50 flex flex-col bg-surface border-l border-border shadow-2xl',
          'transition-transform duration-300 ease-out',
          fullscreen
            ? 'inset-0 top-14'
            : 'right-0 top-14 bottom-0 w-[480px]',
          visible ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <CanvasHeader
          title={title}
          type={artifact.type}
          fullscreen={fullscreen}
          isMobile={false}
          onClose={handleClose}
          onFullscreen={() => setFullscreen(!fullscreen)}
        />

        {/* Tabs */}
        {showTabs && <CanvasTabs tab={tab} setTab={setTab} />}

        {/* Warnings */}
        {externalWarning && !allowExternal && (
          <ExternalWarning onAllow={() => { setAllowExternal(true); setExternalWarning(false) }} />
        )}
        {artifact.type === 'react' && !allowReact && tab === 'preview' && (
          <ReactCdnWarning onAllow={() => setAllowReact(true)} />
        )}

        {/* Content */}
        <CanvasContent
          artifact={artifact}
          tab={tab}
          srcdoc={srcdoc}
          svgDataUri={svgDataUri}
          allowReact={allowReact}
        />
      </div>
    </>
  )
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function CanvasHeader({
  title, type, fullscreen, isMobile, onClose, onFullscreen,
}: {
  title: string
  type: ArtifactContent['type']
  fullscreen: boolean
  isMobile: boolean
  onClose: () => void
  onFullscreen: () => void
}) {
  return (
    <div className="flex items-center justify-between px-4 h-11 border-b border-border shrink-0">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-accent shrink-0">{typeIcon(type)}</span>
        <span className="text-xs font-semibold text-text-primary truncate">{title}</span>
        <Badge variant="muted" className="text-[10px] shrink-0">{typeLabel(type)}</Badge>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!isMobile && (
          <button
            onClick={onFullscreen}
            className="p-1.5 rounded hover:bg-surface-elevated text-text-muted hover:text-text-primary transition-colors"
            title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>
        )}
        <button
          onClick={onClose}
          className="p-1.5 rounded hover:bg-surface-elevated text-text-muted hover:text-text-primary transition-colors"
          title="Close canvas"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

function CanvasTabs({ tab, setTab }: { tab: 'preview' | 'code'; setTab: (t: 'preview' | 'code') => void }) {
  return (
    <div className="flex border-b border-border shrink-0">
      <button
        onClick={() => setTab('preview')}
        className={cn(
          'flex items-center gap-1.5 flex-1 justify-center py-2 text-xs font-medium transition-colors',
          tab === 'preview' ? 'text-accent border-b-2 border-accent' : 'text-text-muted hover:text-text-primary'
        )}
      >
        <Eye className="h-3 w-3" />
        Preview
      </button>
      <button
        onClick={() => setTab('code')}
        className={cn(
          'flex items-center gap-1.5 flex-1 justify-center py-2 text-xs font-medium transition-colors',
          tab === 'code' ? 'text-accent border-b-2 border-accent' : 'text-text-muted hover:text-text-primary'
        )}
      >
        <Code2 className="h-3 w-3" />
        Code
      </button>
    </div>
  )
}

function ExternalWarning({ onAllow }: { onAllow: () => void }) {
  return (
    <div className="mx-3 mt-2 p-2.5 rounded-lg border border-warning/30 bg-warning/5 flex items-start gap-2 shrink-0">
      <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-xs text-warning font-medium">External resources stripped</p>
        <p className="text-[11px] text-text-muted mt-0.5">This content references external URLs, removed for security.</p>
        <button onClick={onAllow} className="mt-1.5 text-[11px] text-accent hover:underline">
          Allow external resources
        </button>
      </div>
    </div>
  )
}

function ReactCdnWarning({ onAllow }: { onAllow: () => void }) {
  return (
    <div className="mx-3 mt-2 p-2.5 rounded-lg border border-info/30 bg-info/5 flex items-start gap-2 shrink-0">
      <Code2 className="h-4 w-4 text-info shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-xs text-info font-medium">React preview requires external CDN</p>
        <p className="text-[11px] text-text-muted mt-0.5">
          Rendering React components loads React 18 + Babel from unpkg.com inside an isolated sandbox.
          The sandbox has no access to your session or dashboard data.
        </p>
        <button onClick={onAllow} className="mt-1.5 text-[11px] text-accent hover:underline">
          Load React preview
        </button>
      </div>
    </div>
  )
}

function CanvasContent({
  artifact, tab, srcdoc, svgDataUri, allowReact,
}: {
  artifact: ArtifactContent
  tab: 'preview' | 'code'
  srcdoc: string
  svgDataUri: string
  allowReact: boolean
}) {
  if (tab === 'code') {
    return (
      <div className="flex-1 overflow-auto">
        <div className="p-3">
          <SyntaxHighlighter
            style={oneDark}
            language={highlightLang(artifact.type)}
            PreTag="div"
            customStyle={{ margin: 0, borderRadius: '8px', fontSize: '12px' }}
          >
            {artifact.content}
          </SyntaxHighlighter>
        </div>
      </div>
    )
  }

  // Preview tab
  if (artifact.type === 'svg') {
    return (
      <div className="flex-1 flex items-center justify-center p-4 bg-white dark:bg-white overflow-auto">
        <img src={svgDataUri} alt="SVG" className="max-w-full max-h-full" />
      </div>
    )
  }

  // React: only render iframe once user has opted in
  if (artifact.type === 'react') {
    if (!allowReact) return <div className="flex-1" />
    return (
      <iframe
        key={artifact.messageId}
        srcDoc={srcdoc}
        // No allow-same-origin: iframe gets opaque origin, cannot access parent frame data
        sandbox="allow-scripts"
        className="flex-1 w-full border-0 bg-white"
        title="React preview"
      />
    )
  }

  return (
    <iframe
      key={artifact.messageId}
      srcDoc={srcdoc}
      // Keep AI-generated HTML on an opaque origin. Forms may render/submit inside the sandbox,
      // but the artifact never receives same-origin access to the OPS dashboard.
      sandbox="allow-scripts allow-forms"
      className="flex-1 w-full border-0 bg-white"
      title={`${artifact.type} preview`}
    />
  )
}

// ── Artifact detection ─────────────────────────────────────────────────────────

interface ToolCall {
  name: string
  status?: string
  result?: string
}

export function detectArtifact(
  content: string,
  messageId: string,
  _toolCalls?: ToolCall[]
): ArtifactContent | null {
  // 1. Explicit <artifact> tag (from system prompt instruction)
  const artifactTag = content.match(/<artifact\s+type="([^"]+)"(?:\s+title="([^"]*)")?\s*>([\s\S]*?)<\/artifact>/i)
  if (artifactTag) {
    const rawType = artifactTag[1].toLowerCase()
    const type: ArtifactContent['type'] = rawType === 'react' || rawType === 'jsx' || rawType === 'tsx'
      ? 'react' : rawType as ArtifactContent['type']
    return { type, content: artifactTag[3].trim(), title: artifactTag[2] || undefined, messageId }
  }

  // 2. Fenced code blocks
  const codeBlock = (lang: string) => {
    const re = new RegExp('```' + lang + '\\s*\\n([\\s\\S]*?)```', 'i')
    const m = content.match(re)
    return m ? m[1].trim() : null
  }

  const html = codeBlock('html')
  if (html) return { type: 'html', content: html, messageId }

  const react = codeBlock('jsx') || codeBlock('tsx') || codeBlock('react')
  if (react) return { type: 'react', content: react, messageId }

  const svg = codeBlock('svg')
  if (svg) return { type: 'svg', content: svg, messageId }

  const css = codeBlock('css')
  if (css) return { type: 'css', content: css, messageId }

  // Image tool calls (generate_image, get_image, get_image_status) are
  // deliberately NOT treated as canvas artifacts. A separate, correct inline
  // renderer (imgThumbnail, in the message bubble itself) already displays
  // generated images directly in the chat -- routing them through the canvas
  // too meant both fired for the same image, competing with whatever HTML/
  // code the canvas was also trying to show at the same time. Removed
  // 2026-08-17 after this was reported as a real, confusing duplication.

  return null
}
